"use client";

import { useEffect } from "react";
import { trackMetaPixelViewContent } from "@/lib/analytics/events";

interface MetaViewContentTrackerProps {
  /** Stable product/content identifier (e.g. product slug). */
  contentId: string;
  /** Human-readable name (e.g. product display name). */
  contentName: string;
  /** Monetary value associated with viewing this content (price in USD). */
  value: number;
  /** Currency code. Defaults to USD. */
  currency?: string;
  /** Meta content type — almost always "product" for our pages. */
  contentType?: "product" | "product_group";
}

/**
 * Fires a single Meta Pixel ViewContent event on mount. Used by routes
 * other than `/product/<slug>/` (which goes through `ProductViewTracker`
 * + `trackProductView` to dual-fire GA4 + PostHog + Meta).
 *
 * This component is Meta-only on purpose: pricing pages and info-product
 * pages don't need a GA4 `view_product` event re-fired alongside (the
 * upstream `/product/<slug>/` page already fired it). Meta's catalog and
 * Lookalike training, however, benefit from a ViewContent on every
 * conversion-intent surface the user lands on.
 */
export default function MetaViewContentTracker({
  contentId,
  contentName,
  value,
  currency = "USD",
  contentType = "product",
}: MetaViewContentTrackerProps) {
  useEffect(() => {
    trackMetaPixelViewContent({
      content_ids: [contentId],
      content_name: contentName,
      content_type: contentType,
      value,
      currency,
    });
  }, [contentId, contentName, value, currency, contentType]);

  return null;
}
